// Multi-territory API endpoints - centralised endpoint definitions
// Mirrors the India pattern in src/endpoints/apiEndpoints.js

export const MT_BASE_URL = import.meta.env.VITE_BASE_URL as string | undefined

const MT_BASE_PATH = 'wht-mt-admin/api/v1'

// Navigation / Sidebar
export const MT_NAVIGATION_API = `${MT_BASE_PATH}/navigation`

// Country Dropdown (generic)
export const MT_COUNTRY_DROPDOWN_API = `wht-mt-admin/api/v1/countrymaster/dropdown`

// Entity Master
export const MT_ENTITY_LIST_API = (countryCode?: string) =>
  countryCode
    ? `${MT_BASE_PATH}/entity-master/list/${countryCode}`
    : `${MT_BASE_PATH}/entity-master/list`
export const MT_ENTITY_SAVE_API = `${MT_BASE_PATH}/entity-master/save`
export const MT_ENTITY_DOWNLOAD_API = `${MT_BASE_PATH}/entity-master/download`
export const MT_ENTITY_DELETE_API = `${MT_BASE_PATH}/entity-master`
export const MT_ENTITY_UPDATE_API = `${MT_BASE_PATH}/entity-master/update`

// Assign Entity
export const MT_ASSIGN_ENTITY_LIST_API = `${MT_BASE_PATH}/assign-entity/list`
export const MT_ASSIGN_ENTITY_DELETE_API = `${MT_BASE_PATH}/assign-entity/delete`
export const MT_ASSIGN_ENTITY_DOWNLOAD_API = `${MT_BASE_PATH}/assign-entity/download`
export const MT_ASSIGN_ENTITY_CREATE_API = `wht-mt-admin/api/v1/user/assign-entity`
export const MT_ENTITY_DROPDOWN_BY_COUNTRY_API = `wht-mt-admin/api/v1/entity-master/dropdown/country-uuids`
export const MT_TAN_DROPDOWN_BY_ENTITY_API = `wht-mt-admin/api/v1/tan-master/dropdown/entities`

// User List
export const MT_USER_LIST_API = `wht-admin/api/v1/user/list`

//  Master Metadata (dynamic country code and data type)
export const MT_MASTER_META_DATA = (countryCode: string, dataType: string) =>
  `${MT_BASE_PATH}/data/${countryCode}/${dataType}/metadata`

// Service Master CRUD (dataType: service-master, dtaa-master, etc.)
// Convert dataType from underscore to hyphen: dtaa_master -> dtaa-master
export const MT_DATA_MASTER_CREATE_API = (dataType: string) =>
  `${MT_BASE_PATH}/${dataType.replace(/_/g, '-')}/add`
export const MT_DATA_MASTER_UPDATE_API = (dataType: string, uuid: string) =>
  `${MT_BASE_PATH}/${dataType.replace(/_/g, '-')}/${uuid}`
export const MT_DATA_MASTER_LIST_API = (dataType: string) =>
  `${MT_BASE_PATH}/${dataType.replace(/_/g, '-')}/list`
export const MT_DATA_MASTER_DOWNLOAD_API = (dataType: string) =>
  `${MT_BASE_PATH}/${dataType.replace(/_/g, '-')}/download`
export const MT_DATA_MASTER_DELETE_API = (dataType: string, uuid: string) =>
  `${MT_BASE_PATH}/${dataType.replace(/_/g, '-')}/${uuid}`

// Bulk Edit / Delete (payee-master, vendor-master, etc.)
export const MT_DATA_MASTER_BULK_EDIT_API = (dataType: string) =>
  `${MT_BASE_PATH}/${dataType.replace(/_/g, '-')}/edit`
export const MT_DATA_MASTER_BULK_DELETE_API = (dataType: string) =>
  `${MT_BASE_PATH}/${dataType.replace(/_/g, '-')}/delete`

// Service Master bulk edit (Nature of service tab - India Section Master parity)
// PUT wht-mt-admin/api/v1/service-master/bulk   (Header: X-Country-Code)
// Body: { entityMasterUuid | tanMasterUuid, entries: [{ uuid, ...changedFields }, ...] }
// All-or-nothing: a 400 means no row was saved.
export const MT_SERVICE_MASTER_BULK_EDIT_API = (dataType: string) =>
  `${MT_BASE_PATH}/${dataType.replace(/_/g, '-')}/bulk`

// Rule Setting (Section Rules)
const MT_RULE_SETTING_BASE = 'wht-mt-data-management/api/v1/section-rules'
export const MT_RULE_SETTING_SEARCH_API = `${MT_RULE_SETTING_BASE}/search`
export const MT_RULE_SETTING_CREATE_API = `${MT_RULE_SETTING_BASE}/create`
export const MT_RULE_SETTING_UPDATE_API = `${MT_RULE_SETTING_BASE}/update`
export const MT_RULE_SETTING_DELETE_API = (ruleUuid: string) =>
  `${MT_RULE_SETTING_BASE}/${ruleUuid}`
export const MT_RULE_SETTING_CONDITIONS_API = (ruleUuid: string) =>
  `${MT_RULE_SETTING_BASE}/conditions/${ruleUuid}`
export const MT_RULE_SETTING_DOWNLOAD_API = `${MT_RULE_SETTING_BASE}/download`
export const MT_RULE_SETTING_SERVICE_VALUES_API = (countryCode: string) =>
  `${MT_RULE_SETTING_BASE}/service-master-values/${countryCode}`

// TAN Master CRUD
export const MT_TAN_MASTER_SAVE_API = `${MT_BASE_PATH}/tan-master/save`
export const MT_TAN_MASTER_UPDATE_API = `${MT_BASE_PATH}/tan-master/update`
export const MT_TAN_MASTER_LIST_API = `${MT_BASE_PATH}/tan-master/global/list`
export const MT_TAN_MASTER_DOWNLOAD_API = `${MT_BASE_PATH}/tan-master/global/download`
export const MT_TAN_MASTER_GET_API = (countryCode: string, uuid: string) =>
  `${MT_BASE_PATH}/tan-master/${countryCode}/${uuid}`
export const MT_TAN_MASTER_DELETE_API = (countryCode: string, uuid: string) =>
  `${MT_BASE_PATH}/tan-master/${countryCode}/${uuid}`

// India TAN Master (used by the embedded India TIN form in the MT flow).
// Save appends the uploaded CSI file name; update appends the record uuid.
export const MT_INDIA_TAN_MASTER_SAVE_API =
  'wht-mt-admin/api/v1/tan-master/india/save'
export const MT_INDIA_TAN_MASTER_UPDATE_API =
  'wht-mt-admin/api/v1/tan-master/india/update'
export const MT_INDIA_TAN_CSI_UPLOAD_API =
  'wht-mt-admin/api/v1/tan-master/upload-csi'
// India TAN detail fetch for the edit form — the tan uuid is the path param.
export const MT_INDIA_TAN_MASTER_GET_API =
  'wht-mt-admin/api/v1/tan-master/india'

// Duplication Rule APIs
export const MT_DUPLICATE_RULE_SAVE_API = `${MT_BASE_PATH}/duplication-rules/addrule`
export const MT_DUPLICATE_RULE_LIST_API = `${MT_BASE_PATH}/duplication-rules/list`
export const MT_DUPLICATE_RULE_DOWNLOAD_API = `${MT_BASE_PATH}/duplication-rules/download`
export const MT_DUPLICATE_RULE_UPDATE_API = `${MT_BASE_PATH}/duplication-rules/updaterule`
export const MT_DUPLICATE_RULE_DELETE_API = `${MT_BASE_PATH}/duplication-rules/delete`
export const MT_TRANSACTION_DATA_META_DATA = (countryCode: string) =>
  `${MT_BASE_PATH}/data/${countryCode}/TRANSACTIONAL_DATA/metadata`

// Screen name mapping for metadata API: bucket/sub-tab -> ?screen= query param value
export const MT_PREPARE_SCREEN_MAP: Record<string, string> = {
  UNPROCESSED: 'PREPARE_UNPROCESSED',
  PROCESSED: 'PREPARE_PROCESSED',
  EXCEPTIONS: 'PREPARE_EXCEPTIONS',
  EXCLUDED: 'PREPARE_EXCLUDED',
  UNMAPPED: 'PREPARE_UNMAPPED',
  MAPPED: 'PREPARE_MAPPED',
}

// Prepare APIs (wht-mt-data-management service)
const MT_PREPARE_BASE = 'wht-mt-data-management/api/v1/prepare'
export const MT_PREPARE_TRANSACTIONAL_DATA_API = (bucket: string) =>
  `${MT_PREPARE_BASE}/transactional-data/${bucket}`
export const MT_PREPARE_BUCKET_COUNTS_API = `${MT_PREPARE_BASE}/bucket-counts`
export const MT_PREPARE_TOTAL_SUM_API = (bucket: string) =>
  `${MT_PREPARE_BASE}/transactional-data/total-sum/${bucket}`
export const MT_PREPARE_NEGATIVE_ENTRIES_API = `${MT_PREPARE_BASE}/negative-entries`
export const MT_PREPARE_EXPORT_API = (bucket: string) =>
  `${MT_PREPARE_BASE}/transactional-data/export-async/${bucket}`
export const MT_PREPARE_REPROCESS_API = (bucket: string) =>
  `${MT_PREPARE_BASE}/transactional-data/reprocess/${bucket}`
export const MT_PREPARE_EXCLUSION_API = (bucket: string) =>
  `${MT_PREPARE_BASE}/exclusion/${bucket}`
export const MT_PREPARE_PERIOD_OPTIONS_API = `${MT_PREPARE_BASE}/period-options`
// Inline (bulk) edit of transactional data rows for a bucket.
// POST wht-mt-data-management/api/v1/prepare/transactional-data/inline-edit/{bucket}
// Header: X-Country-Code
// Body: { edits: [{ uuid, changedCells: { <field_key>: <value>, ... } }, ...] }
// Response: { data: { status, successCount, errorCount, validationErrors, message }, success }
export const MT_PREPARE_INLINE_EDIT_API = (bucket: string) =>
  `${MT_PREPARE_BASE}/transactional-data/inline-edit/${bucket}`

// Upload APIs (wht-mt-data-management service)
const MT_UPLOAD_BASE = 'wht-mt-data-management/api/v1/upload'
export const MT_UPLOAD_FILE_SOURCE_API = `${MT_UPLOAD_BASE}/enums/file-source`
export const MT_UPLOAD_FILE_DATA_TYPE_API = `${MT_UPLOAD_BASE}/enums/file-data-type`
export const MT_UPLOAD_GET_FILE_META_DATA_API = `${MT_UPLOAD_BASE}/get/file-meta-data`
export const MT_UPLOAD_DOWNLOAD_FILE_META_DATA_API = `${MT_UPLOAD_BASE}/download/file-meta-data`
export const MT_UPLOAD_DOWNLOAD_ORIGINAL_FILE_API = `${MT_UPLOAD_BASE}/download/original-file`
export const MT_UPLOAD_DOWNLOAD_ERROR_FILE_API = `${MT_UPLOAD_BASE}/download/error-file`
export const MT_UPLOAD_DELETE_ENTRY_DATA_API = `${MT_UPLOAD_BASE}/delete/entry-data`
export const MT_UPLOAD_DOWNLOAD_TEMPLATE_API = `${MT_UPLOAD_BASE}/download-template`
export const MT_UPLOAD_FILE_API =
  'wht-mt-data-management/api/v1/fileUpload/upload'
export const MT_UPLOAD_SUMMARY_API =
  'wht-mt-data-management/api/v1/fileUpload/summary'

// --- Returns tab / Return Config (MTWHT-RET-01 / PNW-1765) --------------------
// Configuration-driven Returns screen. Filters + grid columns (headers) + field
// mapping + form layout are driven by the Return Config API (PNW-1724 / MTWHT-BC-09).

// Combined Returns-tab config â€” a SINGLE call that returns both the filter
// definitions and the grid column definitions for the country. Replaces the
// separate filters + headers calls on the Returns tab.
// GET wht-mt-admin/api/v1/return-view/config  (Headers: Authorization, X-Country-Code)
export const MT_RETURN_VIEW_CONFIG_API =
  'wht-mt-admin/api/v1/return-view/config'

// Distinct dropdown values for each configured filter (filter options).
// Optional dependent filters may be passed as extra query params (e.g. &entity_name=Acme)
// to narrow the results. GET wht-mt-admin/api/v1/return-view/filter-data
export const MT_RETURN_VIEW_FILTER_DATA_API =
  'wht-mt-admin/api/v1/return-view/filter-data'

// Generate a return â€” creates/replaces a returns_tracking row and runs
// config-driven generation. The return form is passed as the ?form= query param;
// everything else about HOW to generate comes from config.
// POST wht-mt-admin/api/v1/return-view/generate?form=<FORM>
export const MT_RETURN_VIEW_GENERATE_API =
  'wht-mt-admin/api/v1/return-view/generate'

// Status grid (list) â€” paginated returns_tracking grid. page/pageSize are query
// params; body carries equality filters + searchCOList (full operator set, AND).
// POST wht-mt-admin/api/v1/return-view/grid?page=1&pageSize=100
export const MT_RETURN_VIEW_GRID_API = 'wht-mt-admin/api/v1/return-view/grid'

// Refresh (regenerate in place) an existing tracking row. Same response as generate.
// POST wht-mt-admin/api/v1/return-view/refresh/{uuid}
export const MT_RETURN_VIEW_REFRESH_API = (uuid: string) =>
  `wht-mt-admin/api/v1/return-view/refresh/${uuid}`

// View a generated return (open its output), tab-wise. The tab_key (FORM /
// RETURN_DETAILS, from the metadata layout) and the row uuid are on the path,
// so the BE returns only the data for the requested tab.
// GET wht-mt-admin/api/v1/return-view/view/{tab}/{uuid}
export const MT_RETURN_VIEW_VIEW_API = (tab: string, uuid: string) =>
  `wht-mt-admin/api/v1/return-view/view/${tab}/${uuid}`

// Save edits for a generated return. The tab_key (FORM / RETURN_DETAILS, from
// the metadata layout) and the row uuid are both on the path, so the BE knows
// which tab's data is being saved. Body carries one entry per changed row:
// { uuid, values }. A single call supports bulk edit — multiple RETURN_DETAILS
// line items (or the one FORM row) can be saved together in one `rows` array.
// PUT wht-mt-admin/api/v1/return-view/edit/{tab}/{uuid}
export const MT_RETURN_VIEW_EDIT_API = (tab: string, uuid: string) =>
  `wht-mt-admin/api/v1/return-view/edit/${tab}/${uuid}`

// Layout metadata for the return form/details tabs (fetched once on mount).
// GET wht-mt-admin/api/v1/return-view/metadata  (Headers: Authorization, X-Country-Code)
export const MT_RETURN_VIEW_METADATA_API =
  'wht-mt-admin/api/v1/return-view/metadata'

// Async download (id-based, India/Prepare parity). Enqueue only (downloads panel resolves the file).
// 7a POST wht-mt-data-management/api/v1/returns/download-async?form=<FORM> { returnGenerationUuid }
export const MT_RETURNS_DOWNLOAD_ASYNC_API =
  'wht-mt-data-management/api/v1/returns/download-async'

// Suggest WHT/TDS Rate — triggers rate determination for the selected entities/periods
export const MT_PREPARE_CALCULATE_RATE_API =
  'wht-mt-data-management/api/v1/fileUpload/calculateRate'

// Rate determination status — drives the enabled/disabled state of the
// Suggest WHT/TDS Rate button. Called alongside every VTD (transactional-data)
// list fetch. Response: { data: { rateDeterminationInProgress, inProgressCount,
// buttonEnabled }, success }.
export const MT_PREPARE_RATE_DETERMINATION_STATUS_API =
  'wht-mt-data-management/api/v1/rate-determination/status'

// --- Compute tab (MTWHT-CMP-01 / PNW-1764) -----------------------------------
// Configuration-driven Compute screen. Columns come from the country's Compute
// Configuration (summary-config); the grouped/summed data + totals + Excel export
// come from the data-management compute transactional-data endpoints.

// Compute summary configuration (drives the grid columns — dimensions + measures).
// GET wht-mt-admin/api/v1/compute-config/{countryCode}/summary-config
export const MT_COMPUTE_SUMMARY_CONFIG_API = (countryCode: string) =>
  `${MT_BASE_PATH}/compute-config/${countryCode}/summary-config`

const MT_COMPUTE_BASE =
  'wht-mt-data-management/api/v1/compute/transactional-data'

// Year/month-wise grouped computed rows (bucket is PROCESSED for the Compute tab).
// POST wht-mt-data-management/api/v1/compute/transactional-data/{bucket}
export const MT_COMPUTE_DATA_API = (bucket: string) =>
  `${MT_COMPUTE_BASE}/${bucket}`

// Total sum of all records (totals row), summed per SUM column.
// POST wht-mt-data-management/api/v1/compute/transactional-data/total-sum/{bucket}
export const MT_COMPUTE_TOTAL_SUM_API = (bucket: string) =>
  `${MT_COMPUTE_BASE}/total-sum/${bucket}`

// Async Excel download of the computed grid (honours the current filter/search).
// POST wht-mt-data-management/api/v1/compute/transactional-data/excel/{bucket}
export const MT_COMPUTE_EXCEL_API = (bucket: string) =>
  `${MT_COMPUTE_BASE}/excel/${bucket}`
